import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@gov-nx/core/service';
import type { NotificationsContexts, PageCode } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	DataTable,
	LayoutGap,
	Icon,
} from '@gov-nx/ui/web';
import { sanitizedText, toDateReadable } from '@gov-nx/utils/common';
import { NotificationsSkeleton } from './NotificationsSkeleton';

interface ModalViewProps {
	selectedNotification: NotificationsContexts['selectedNotification'];
	code: PageCode;
	children: ReactNode;
}

export const ModalView = ({
	selectedNotification,
	code,
	children,
}: ModalViewProps) => {
	const { t: ts } = useLocale(code);
	const { isFetching, detail, links, poLinks } = selectedNotification;

	const cleanedText = sanitizedText(links, detail?.popisDlouhy);

	return (
		<LayoutGap>
			{isFetching ? (
				<NotificationsSkeleton />
			) : (
				<>
					<DataTable responsive>
						<tbody>
							<tr>
								<td className="w-[20%]">{ts('zaznam.termin')}</td>
								<td className="w-[80%]">
									<strong>
										{detail?.datumACasVytvoreni
											? toDateReadable(detail?.datumACasVytvoreni)
											: '-'}
									</strong>
								</td>
							</tr>
							<tr>
								<td className="align-top">{ts('zaznam.odesilatel')}</td>
								<td>
									<strong>{detail?.odesilatelNazev ?? '-'}</strong>
								</td>
							</tr>
							<tr>
								<td className="align-top">{ts('zaznam.predmet')}</td>
								<td>
									<strong dangerouslySetInnerHTML={{ __html: cleanedText }} />
								</td>
							</tr>
							{detail?.rozsirujiciInformace?.statusText ? (
								<tr>
									<td>{ts('zaznam.stav')}</td>
									<td>
										<strong>{detail.rozsirujiciInformace.statusText}</strong>
									</td>
								</tr>
							) : null}
						</tbody>
					</DataTable>
					<ButtonGroupLayout>
						{poLinks?.map((linkData) => (
							<Button
								key={linkData?.['href-key']}
								type="solid"
								color="primary"
								size="m"
								isExpandedMobile
								asChild>
								<Link to={linkData?.link as string}>{linkData?.label}</Link>
							</Button>
						))}
						{links?.map((linkData) => (
							<Button
								asChild
								type="solid"
								color="primary"
								size="m"
								iconStart={
									<Icon
										type="components"
										name="box-arrow-up-right"
									/>
								}
								isExpandedMobile
								key={linkData.key}>
								<Link
									to={linkData?.href || ''}
									target="_blank">
									{ts('akce.prejit-na-web')}
								</Link>
							</Button>
						))}
						{children}
					</ButtonGroupLayout>
				</>
			)}
		</LayoutGap>
	);
};
